/**
 * Helpers for rendering and aggregating IANA timezone identifiers.
 * Consumed by EntityTeamCompositionCard on the Group page. Lives in `shared/`
 * rather than co-located so future consumers (e.g. user-page summaries) can
 * adopt the same formatting without duplication.
 */

/** Returns the GMT/UTC offset string for an IANA timezone at the current time, e.g. "GMT+1" or "" if invalid. */
export function getTimezoneOffset(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Format an IANA timezone for display:
 *  - "Europe/London (GMT+1)" when an offset can be computed
 *  - the raw `tz` string as a fallback when offset lookup fails (e.g.
 *    unsupported `shortOffset` format on older runtimes, or an IANA name
 *    the runtime doesn't recognise — rare in practice)
 *  - `undefined` only when `tz` is empty/undefined
 */
export function formatTimezone(tz: string | undefined): string | undefined {
  if (!tz) return undefined;
  const offset = getTimezoneOffset(tz);
  return offset ? `${tz} (${offset})` : tz;
}

/**
 * Parses "GMT+1", "GMT-5", "GMT+5:30" → minutes offset from UTC.
 * Returns undefined if the string isn't a recognisable GMT offset.
 */
export function gmtOffsetMinutes(offset: string): number | undefined {
  // Common Intl outputs: "GMT", "GMT+1", "GMT-5", "GMT+5:30", "UTC", "UTC+1"
  const m = offset.match(/^(?:GMT|UTC)([+-]\d{1,2})(?::(\d{2}))?$/);
  if (!m) return offset === 'GMT' || offset === 'UTC' ? 0 : undefined;
  const hours = parseInt(m[1], 10);
  const mins = m[2] ? parseInt(m[2], 10) : 0;
  return hours * 60 + (hours < 0 ? -mins : mins);
}

/**
 * Format a non-negative duration in minutes as "Xh", "Xh Ym", or "Ym".
 * Used to render timezone spans across zones that aren't whole-hour offsets
 * (e.g. Asia/Kolkata is UTC+5:30; spread to London = "4h 30m", not "5h").
 */
export function formatHourSpan(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(m / 60);
  const remainder = m - h * 60;
  if (h === 0) return `${remainder}m`;
  if (remainder === 0) return `${h}h`;
  return `${h}h ${remainder}m`;
}

/**
 * Summarises a set of IANA timezones into a single human-readable phrase.
 *  - Empty input: undefined
 *  - All same: "Europe/London (GMT+1)"
 *  - Spread: "Spans 4h 30m · Asia/Kolkata → Europe/London"
 */
export function summariseTimezones(tzs: string[]): string | undefined {
  const valid = tzs.filter(Boolean);
  if (valid.length === 0) return undefined;

  const unique = Array.from(new Set(valid));
  if (unique.length === 1) {
    return formatTimezone(unique[0]);
  }

  const withOffsets = unique
    .map(tz => ({ tz, offset: gmtOffsetMinutes(getTimezoneOffset(tz)) }))
    .filter(x => x.offset !== undefined) as Array<{ tz: string; offset: number }>;

  if (withOffsets.length === 0) return undefined;

  withOffsets.sort((a, b) => a.offset - b.offset);
  const min = withOffsets[0];
  const max = withOffsets[withOffsets.length - 1];
  const spanMinutes = max.offset - min.offset;
  if (spanMinutes === 0) {
    // Multiple distinct IANA names that resolve to the same wall-clock offset
    // (e.g. Europe/London + Europe/Lisbon = GMT+0). Renders as a single
    // representative zone rather than a "Spans 0h" phrase — the latter would
    // be misleading; "everyone shares the same offset" is the useful summary.
    return formatTimezone(min.tz);
  }
  return `Spans ${formatHourSpan(spanMinutes)} · ${min.tz} → ${max.tz}`;
}
